import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Bell,
  BellOff,
  CheckCircle2,
  X,
  Pencil,
  ClipboardList,
  Play,
  PauseCircle,
} from "lucide-react";
import { Badge } from "./ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";
import { Separator } from "./ui/separator";
import notificationService from "../services/notificationService";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Skeleton } from "./common/Skeleton";
import { formatDateTime } from "../utils/formatters";

const NotificationBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const intervalRef = useRef(null);

  // Load notifications and unread count
  const loadNotifications = useCallback(async () => {
    if (!user?.user_id) return;

    try {
      setLoading(true);
      const [notificationsResponse, countResponse] = await Promise.all([
        notificationService.getNotifications({
          page: 1,
          limit: 10,
          is_read: false,
        }),
        notificationService.getUnreadCount(),
      ]);

      if (notificationsResponse.success) {
        setNotifications(notificationsResponse.data?.notifications || []);
      }

      if (countResponse.success) {
        setUnreadCount(countResponse.data?.count || 0);
      }
    } catch (error) {
      console.error("Error loading notifications:", error);
    } finally {
      setLoading(false);
    }
  }, [user?.user_id]);

  // Load notifications on mount and set up polling
  useEffect(() => {
    if (user?.user_id) {
      loadNotifications();

      // Poll for new notifications every 30 seconds
      intervalRef.current = setInterval(() => {
        loadNotifications();
      }, 30000);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [user?.user_id, loadNotifications]);

  const handleMarkAsRead = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await notificationService.markAsRead(notificationId);
      setNotifications((prev) =>
        prev.map((n) =>
          n.notification_id === notificationId ? { ...n, is_read: true } : n,
        ),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error marking notification as read:", error);
      toast.error("Failed to mark notification as read");
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success("All notifications marked as read");
    } catch (error) {
      console.error("Error marking all as read:", error);
      toast.error("Failed to mark all notifications as read");
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read if unread
    if (!notification.is_read) {
      try {
        await notificationService.markAsRead(notification.notification_id);
        setNotifications((prev) =>
          prev.map((n) =>
            n.notification_id === notification.notification_id
              ? { ...n, is_read: true }
              : n,
          ),
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (error) {
        console.error("Error marking notification as read:", error);
      }
    }

    // Navigate based on notification type
    if (notification.test_run_id && notification.project_id) {
      // Navigate to test run execution page
      const params = new URLSearchParams({
        test_run_id: notification.test_run_id,
        project_id: notification.project_id,
      });
      navigate(`/test-run-execution?${params.toString()}`);
    } else if (notification.test_case_id && notification.project_id) {
      const params = new URLSearchParams({
        project_id: notification.project_id,
        test_case_id: notification.test_case_id,
      });

      if (
        notification.type === "test_case_assigned_for_review" ||
        notification.type === "test_case_updated_after_changes_requested"
      ) {
        navigate(`/review-test-cases?${params.toString()}`);
      } else {
        navigate(`/test-cases?${params.toString()}`);
      }
    }

    setOpen(false);
  };

  const getNotificationIcon = (type, notification = null) => {
    switch (type) {
      case "test_case_approved":
        return <CheckCircle2 className="w-5 h-5 text-green-600" />;
      case "test_case_rejected":
        return <X className="w-5 h-5 text-red-600" />;
      case "test_case_changes_requested":
        return <Pencil className="w-5 h-5 text-yellow-600" />;
      case "test_case_assigned_for_review":
        return <ClipboardList className="w-5 h-5 text-blue-600" />;
      case "test_case_updated_after_changes_requested":
        return <Pencil className="w-5 h-5 text-orange-600" />;
      case "test_run_assigned":
        return <Play className="w-5 h-5 text-blue-600" />;
      case "test_run_status_changed":
        // Check if the notification is about completion
        if (notification) {
          const title = (notification.title || "").toLowerCase();
          const message = (notification.message || "").toLowerCase();
          if (title.includes("completed") || message.includes("completed")) {
            return <CheckCircle2 className="w-5 h-5 text-green-600" />;
          } else if (
            title.includes("suspended") ||
            message.includes("suspended") ||
            title.includes("paused") ||
            message.includes("paused")
          ) {
            return <PauseCircle className="w-5 h-5 text-yellow-600" />;
          } else if (
            title.includes("cancelled") ||
            message.includes("cancelled")
          ) {
            return <X className="w-5 h-5 text-red-600" />;
          }
        }
        return <PauseCircle className="w-5 h-5 text-purple-600" />;
      default:
        return <Bell className="w-5 h-5 text-text-secondary" />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case "test_case_approved":
        return "bg-success-light border-success";
      case "test_case_rejected":
        return "bg-error-light border-error";
      case "test_case_changes_requested":
        return "bg-warning-light border-warning";
      case "test_case_assigned_for_review":
        return "bg-info-light border-info";
      case "test_case_updated_after_changes_requested":
        return "bg-warning-light border-warning";
      case "test_run_assigned":
        return "bg-info-light border-info";
      case "test_run_status_changed":
        return "bg-primary-100 border-primary-300";
      default:
        return "bg-bg-hover border-border-primary";
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(newOpen) => {
        setOpen(newOpen);
        if (newOpen) {
          loadNotifications(); // Refresh when opening
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label="notifications"
        >
          {unreadCount > 0 ? (
            <>
              <Bell className="w-6 h-6 text-text-secondary" />
              <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-red-600 text-white">
                {unreadCount > 99 ? "99+" : unreadCount}
              </Badge>
            </>
          ) : (
            <BellOff className="w-6 h-6 text-text-secondary" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] max-h-[600px] p-0" align="end">
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-text-primary">Notifications</h3>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleMarkAllAsRead}
                className="text-xs h-auto p-1 text-primary-600 hover:text-primary-700"
              >
                Mark all as read
              </Button>
            )}
          </div>

          <Separator className="mb-2" />

          {loading ? (
            <div className="py-4 px-2 space-y-3">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-8">
              <BellOff className="w-12 h-12 text-text-tertiary mx-auto mb-2" />
              <p className="text-sm text-text-tertiary">No notifications</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {notifications.map((notification) => (
                <div
                  key={notification.notification_id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                    notification.is_read
                      ? "bg-bg-primary border-border-primary"
                      : `${getNotificationColor(notification.type)} font-semibold`
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5">
                      {getNotificationIcon(notification.type, notification)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm ${notification.is_read ? "text-text-secondary" : "text-text-primary"} font-medium`}
                      >
                        {notification.title}
                      </p>
                      <p className="text-sm text-text-secondary mt-1">
                        {notification.message}
                      </p>
                      {notification.test_case_title && (
                        <p className="text-xs text-text-tertiary mt-1">
                          Test Case: {notification.test_case_title}
                        </p>
                      )}
                      <p className="text-xs text-text-tertiary mt-1">
                        {formatDateTime(notification.created_at)}
                      </p>
                    </div>
                    {!notification.is_read && (
                      <button
                        onClick={(e) =>
                          handleMarkAsRead(notification.notification_id, e)
                        }
                        className="mt-0.5 p-1 rounded hover:bg-bg-hover"
                        title="Mark as read"
                      >
                        <div className="w-2 h-2 bg-indigo-600 rounded-full"></div>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
